import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import CompetitorLogo from '@/features/sports/components/ui/CompetitorLogo.vue';
import NoOfferNotifier from '@/features/sports/components/global/NoOfferNotifier.vue';
import GameEventStatus from '@/features/sports/components/ui/GameEventStatus.vue';

import { ObjectType, SingleStepSportEvent, SportEventStatus, SportOffer } from '@/@types/graphql-types';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { BuyboxParam, trackClickOutSports } from '@/helpers/offer-helper';
import { SingleStepContext } from '@/features/sports/helpers/config';
import { formatDate, formatTime } from '@/helpers/time-zone-helper';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useAppStore } from '@/stores/pinia/useAppStore';
import SmartLink from '@/components/nav/links/SmartLink.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'SingleStepEventEncounter',
  props: {
    context: null,
    event: null,
    listIndex: null,
    isHeadToHeadModule: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
	context: SingleStepContext;
	event: SingleStepSportEvent;
	listIndex: number;
	isHeadToHeadModule?: boolean;
};



const { webLocale, locales, language, country } = storeToRefs(useLanguageStore());
const { timeZone } = storeToRefs(useUserStore());
const { xServeTrack } = storeToRefs(useAppStore());
const { tc } = useI18n();
const componentContainer = computed(() => {
	if (props.event.content.fullPath) {
		return {
			cmp: SmartLink,
			props: {
				name: 'app.sports.subdomain.event',
				to: props.event.content.fullPath,
			},
		};
	}
	return {
		cmp: 'div',
	};
});

const sportContext = computed(() => {
	return SnowplowSportContext.fromSportEventV2(props.event.sport, ObjectType.SportEvent, props.event);
});

const competitors = computed(() => {
	return props.context.getCompetitors(props.event, webLocale.value);
});

const isDoublesEvent = computed(() => {
	return competitors.value.start.length === 2 && competitors.value.end.length === 2;
});

const startName = computed(() => {
	return competitors.value.start.map(team => team.name).join(' / ');
});

const endName = computed(() => {
	return competitors.value.end.map(team => team.name).join(' / ');
});

const start = computed(() => {
	return competitors.value.start[0];
});

const end = computed(() => {
	return competitors.value.end[0];
});

const eventStatus = computed(() => {
	return props.event.content.status;
});

const isLive = computed(() => {
	return eventStatus.value === SportEventStatus.Live;
});

const isEnded = computed(() => {
	return [SportEventStatus.Ended, SportEventStatus.Closed].includes(eventStatus.value);
});

const isNotUpcoming = computed(() => {
	return [
		SportEventStatus.Cancelled,
		SportEventStatus.Closed,
		SportEventStatus.Delayed,
		SportEventStatus.Ended,
		SportEventStatus.Live,
		SportEventStatus.Postponed,
		SportEventStatus.Suspended,
	].includes(eventStatus.value);
});

const isNotNBA = computed(() => {
	if (props.event.competition?.id === 'scn-bkb-8331232') return false;
	return true;
});

const eventDate = computed(() => {
	return formatDate(
		new Date(props.event.content.startAt),
		locales.value,
		webLocale.value,
		timeZone.value,
		language.value
	);
});

const eventTime = computed(() => {
	return formatTime(
		new Date(props.event.content.startAt),
		locales.value,
		webLocale.value,
		timeZone.value,
		language.value
	);
});

const watchNowOffer = computed(() => {
	return props.event.watchNowOffer as SportOffer | undefined;
});

const finishedYear = computed(() => {
	return Intl.DateTimeFormat(country.value, { year: 'numeric' }).format(new Date(props.event.content.startAt));
});

const finishedDate = computed(() => {
	return Intl.DateTimeFormat(country.value, { day: '2-digit', month: 'short' }).format(
		new Date(props.event.content.startAt)
	);
});

const notifyMePayload = computed(() => {
	return {
		eventId: props.event.id,
		isNotificationActive: !!props.event.mysportlistEntry?.createdAt,
	};
});

function getMonetizationType(offer: SportOffer) {
	if (offer?.package?.packageId === 695 && country.value === 'FR') return tc('WEBAPP_FREE_TRIAL').toUpperCase();

	return offer.monetizationType;
}

const offerClickoutUrl = computed(() => {
	return new ClickoutUrl(watchNowOffer.value)
		.addContexts(sportContext.value)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Sport)
		.set('uct_sport', props.event?.sport)
		.setCanary(xServeTrack.value);
});

const teamNameLink = computed(() => {
	return {
		start: start.value.fullPath !== '' ? start.value.fullPath : props.event.content.fullPath ?? '',
		end: end.value.fullPath !== '' ? end.value.fullPath : props.event.content.fullPath ?? '',
	};
});

function handleClickOut(offer: SportOffer | undefined, listIndex: number) {
	if (offer == null) return;

	trackClickOutSports(offer, 0, listIndex, offer.sportObjectType, 'card-v2', [sportContext.value]);
}

function handleCardClick() {
	TrackingHelper.trackEvent('userinteraction', { action: 'event_clicked', property: 'card-v2', useLastRoute: true }, [
		sportContext.value,
	]);
}

function handleNoOfferNotifierClick() {
	TrackingHelper.trackEvent('userinteraction', { action: 'notify_me_clicked', property: 'card-v2' }, [
		sportContext.value,
	]);
}

function onCompetitionClick() {
	// tracking
	const sportContext = new SnowplowSportContext(props.event.sport, ObjectType.SportCompetition);

	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'competition_clicked', label: props.context.pageType, property: 'header', useLastRoute: true },
		[sportContext]
	);
}

return { __sfc: true,props, webLocale, locales, language, country, timeZone, xServeTrack, tc, componentContainer, sportContext, competitors, isDoublesEvent, startName, endName, start, end, eventStatus, isLive, isEnded, isNotUpcoming, isNotNBA, eventDate, eventTime, watchNowOffer, finishedYear, finishedDate, notifyMePayload, getMonetizationType, offerClickoutUrl, teamNameLink, handleClickOut, handleCardClick, handleNoOfferNotifierClick, onCompetitionClick, ProviderIcon, CompetitorLogo, NoOfferNotifier, GameEventStatus }
}

})